import { useEffect, useRef, useState } from "react";
import type { Observation, SessionHeader, Turn } from "../../models";
import type { SessionSource } from "../../sources/types";

export interface PlayerState {
  status: "loading" | "ready" | "finished" | "failed";
  header: SessionHeader | null;
  /** All turns currently visible. In replay this is the full transcript;
   *  in live it grows as the subscription pushes events. */
  turns: Turn[];
  observations: Observation[];
  error: string | null;
}

export function useSessionPlayer(sessionId: string, source: SessionSource): PlayerState {
  const [state, setState] = useState<PlayerState>({
    status: "loading",
    header: null,
    turns: [],
    observations: [],
    error: null,
  });

  const subRef = useRef<{ unsubscribe(): void } | null>(null);

  useEffect(() => {
    let cancelled = false;
    subRef.current?.unsubscribe();
    subRef.current = null;

    setState({ status: "loading", header: null, turns: [], observations: [], error: null });

    (async () => {
      try {
        const header = await source.loadHeader(sessionId);
        if (cancelled) return;
        const snap = await source.snapshot(sessionId);
        if (cancelled) return;

        setState({
          status: snap.closed ? "finished" : "ready",
          header,
          turns: snap.turns,
          observations: snap.observations,
          error: null,
        });

        if (source.mode === "live" && source.subscribe) {
          const sub = source.subscribe(sessionId);
          sub.onTurn((t) => setState((s) => ({ ...s, turns: [...s.turns, t] })));
          sub.onObservation((o) =>
            setState((s) => ({ ...s, observations: [...s.observations, o] })),
          );
          sub.onClose(() => setState((s) => ({ ...s, status: "finished" })));
          subRef.current = sub;
        }
      } catch (err) {
        if (cancelled) return;
        setState((s) => ({ ...s, status: "failed", error: (err as Error).message }));
      }
    })();

    return () => {
      cancelled = true;
      subRef.current?.unsubscribe();
      subRef.current = null;
      source.close();
    };
  }, [sessionId, source]);

  return state;
}
